import { create } from 'zustand';
import { getProjectsScheme, getProjectSchemeById, getAssociatedProjects, hasProjectAccess } from '../v2/apis/projectsSchemeApi';
import { BoardSchemeInterface, ProjectSchemeInterface } from 'ts-interfaces';
import { CollectionInterface, Permissions } from 'ts-interfaces';
import { getBoardsForProject, getBoardsForProjects } from '../v2/apis/boardsSchemeApi';
import { getCollectionsForProject, getCollectionsForProjects } from '../v2/apis/collectionsSchemeApi';
import { firebaseHelper } from "ui";
import { getDesignFirmsOfUser, getDesignersWithAccess } from '../v2/apis/teamsApi';

interface ProjectsState {
  projects: ProjectSchemeInterface[];
  activeProjects: ProjectSchemeInterface[];
  ownedProjects: ProjectSchemeInterface[];
  sharedProjects: ProjectSchemeInterface[];
  isProjectsLoading: boolean;
  projectsLoaded: boolean;
  loadedUid: string | null;
  sharedProjectIds: string[];

  invalidateCache: () => void;
  setProject: (project: ProjectSchemeInterface) => void;
  setProjects: (projects: ProjectSchemeInterface[]) => void;
  addProject: (project: ProjectSchemeInterface) => void;
  setSharedProjectIds: (ids: string[]) => void;
  getProjectOwner: (projectId: string) => string | undefined;
  getProjectSchemas: (uid: string, sharedProjectIds?: string[], permissions?: any) => Promise<void>;
  updateBoardInProjects: (board: BoardSchemeInterface) => void;
  removeBoardFromProjects: (boardId: string) => void;
  updateCollectionInProjects: (collection: CollectionInterface) => void;
  removeCollectionFromProjects: (collectionId: string) => void;
}

interface ProjectDetails {
  project: ProjectSchemeInterface;
  boards: BoardSchemeInterface[];
  collections: CollectionInterface[];
  isLoading: boolean;
}

interface ProjectSchemaDetailsState {
  projectsData: Record<string, ProjectDetails>;
  loadedUids: Record<string, string>; 
  userProjects: ProjectSchemeInterface[];
  ownedProjects: ProjectSchemeInterface[];
  sharedProjects: ProjectSchemeInterface[];
  setBoards: (projectId: string, boards: BoardSchemeInterface[], uid: string, permissions: Permissions | null) => Promise<void>;
  setCollections: (projectId: string, collections: CollectionInterface[], uid: string, permissions: Permissions | null) => Promise<void>;
  setProject: (projectId: string, project: ProjectSchemeInterface) => void;
  getProjectDetails: (project: ProjectSchemeInterface, uid: string, permissions: Permissions | null) => Promise<void>;
  getProjectById: (projectId: string) => ProjectDetails | null;
  syncUserProjects: () => void;
}

interface ProjectManagementState {
  selectedProject: ProjectSchemeInterface | null;
  designFirms: any[];
  teamMembers: any[];
  associatedProjectsList: any[];
  error: string | null;
  setProjectData: (data: Partial<ProjectManagementState>) => void;
  resetProjectData: () => void;
  fetchProjectDataById: (id: string, uid: string, permissions: Permissions | null) => Promise<void>;
}

// Helper function to sync changes to useProjectSchemaDetails
const syncToProjectDetails = (projectId: string, updatedBoards: BoardSchemeInterface[], updatedCollections: CollectionInterface[]) => {
  const projectDetailsState = useProjectSchemaDetails.getState();
  if (projectDetailsState.projectsData[projectId]) {
    useProjectSchemaDetails.setState(state => ({
      projectsData: {
        ...state.projectsData,
        [projectId]: {
          ...state.projectsData[projectId],
          boards: updatedBoards.filter(board => board.archive !== true),
          collections: updatedCollections
        }
      }
    }));
  }
};

export const useProjectSchemas = create<ProjectsState>((set, get) => ({
  projects: [],
  activeProjects: [],
  ownedProjects: [],
  sharedProjects: [],
  isProjectsLoading: false,
  projectsLoaded: false,
  loadedUid: null,
  sharedProjectIds: [],

  invalidateCache: () => {
    set({ loadedUid: null, projectsLoaded: false });
  },
  
  setProject: (project) => {
    set((state) => {
      const projects = state.projects.map(p => 
        p.id === project.id ? project : p
      );
      
      const activeProjects = projects.filter(p => !p.archive);
      const ownedProjects = projects.filter(p => p.owner === state.loadedUid && !p.archive);
      const sharedProjects = projects.filter(p => 
        p.owner !== state.loadedUid && !p.archive && state.sharedProjectIds.includes(p.id)
      );
      
      return { projects, activeProjects, ownedProjects, sharedProjects };
    });
  },
  
  setProjects: (projects) => set({ projects }),

  addProject: (project: ProjectSchemeInterface) => {
    set((state) => {
      // New projects start with empty boards array
      const newProject = { ...project, boards: [], collections: [] };
      const newProjects = [newProject, ...state.projects];
      const activeProjects = newProjects.filter(p => !p.archive);
      const ownedProjects = newProjects.filter(p => p.owner === project.owner && !p.archive);
      
      return {
        projects: newProjects,
        activeProjects,
        ownedProjects,
        sharedProjects: state.sharedProjects,
      };
    });
  },

  setSharedProjectIds: (ids) => set({ sharedProjectIds: ids }),

  // Helper function to get project owner by project ID
  getProjectOwner: (projectId: string): string | undefined => {
    const { projects } = get();
    return projects.find(p => p.id === projectId)?.owner;
  },
  
  getProjectSchemas: async (uid: string, sharedProjectIds: string[] = [], permissions?: any) => {
    const { loadedUid, isProjectsLoading } = get();

    // Check if we need to reload
    const currentSharedIds = get().sharedProjectIds;
    const sharedIdsChanged = JSON.stringify(sharedProjectIds.sort()) !== JSON.stringify(currentSharedIds.sort());
    
    if (!uid || (uid === loadedUid && !sharedIdsChanged) || isProjectsLoading) return;
    
    try {
      set({ isProjectsLoading: true });

      // Fetch projects (without boards)
      const projectSchemas = await getProjectsScheme(uid, sharedProjectIds);
      
      // Fetch boards for all projects
      const projectIds = projectSchemas.map(p => p.id);
      const [boardsByProject, collectionsByProject] = await Promise.all([
        getBoardsForProjects(projectIds, uid, permissions),
        getCollectionsForProjects(projectIds, uid, permissions)
      ]);      
      
      // Add boards and collections to each project
      const projectsWithBoardsAndCollections = projectSchemas.map(project => ({
        ...project,
        boards: boardsByProject.get(project.id) || [],
        collections: collectionsByProject.get(project.id) || []
      }));
      
      const activeProjects = projectsWithBoardsAndCollections.filter(project => !project.archive);
      const ownedProjects = projectsWithBoardsAndCollections.filter(project => project.owner === uid && !project.archive);
      const sharedProjects = projectsWithBoardsAndCollections.filter(project => 
        project.owner !== uid && !project.archive && sharedProjectIds.includes(project.id)
      );

      set({ 
        projects: projectsWithBoardsAndCollections,
        activeProjects,
        ownedProjects,
        sharedProjects,
        isProjectsLoading: false,
        projectsLoaded: true,        
        loadedUid: uid,
        sharedProjectIds
      });
    } catch (error) {
      console.error('Error fetching project schemas:', error);
      set({ isProjectsLoading: false });
    }
  },
  
  // Update a board across all projects (for when board is renamed, thumbnail changed, etc.)
  updateBoardInProjects: (board: BoardSchemeInterface) => {
    set((state) => {
      if (!board.associatedProjectId) return state;
      
      // Update the board in its associated project
      const projects = state.projects.map(project => {
        if (project.id === board.associatedProjectId) {
          const boards = project.boards || [];
          const updatedBoards = boards.map(b => b.id === board.id ? board : b);
          
          // If board doesn't exist in the project, add it
          if (!boards.some(b => b.id === board.id)) {
            updatedBoards.push(board);
          }
          
          syncToProjectDetails(project.id, updatedBoards, project.collections || []);
          return { ...project, boards: updatedBoards };
        }
        return project;
      });
      
      const activeProjects = projects.filter(p => !p.archive);
      const ownedProjects = projects.filter(p => p.owner === state.loadedUid && !p.archive);
      const sharedProjects = projects.filter(p => 
        p.owner !== state.loadedUid && !p.archive && state.sharedProjectIds.includes(p.id)
      );
      
      return {
        projects,
        activeProjects,
        ownedProjects,
        sharedProjects
      };
    });
  },
  
  // Remove a board from all projects (for when board is deleted or association removed)
  removeBoardFromProjects: (boardId: string) => {
    set((state) => {
      // Remove the board from all projects
      const projects = state.projects.map(project => {
        if (project.boards && project.boards.some(b => b.id === boardId)) {

          const updatedBoards = project.boards.filter(b => b.id !== boardId);
          syncToProjectDetails(project.id, updatedBoards, project.collections || []);

          return {
            ...project,
            boards: updatedBoards
          };
        }
        return project;
      });
      
      const activeProjects = projects.filter(p => !p.archive);
      const ownedProjects = projects.filter(p => p.owner === state.loadedUid && !p.archive);
      const sharedProjects = projects.filter(p => 
        p.owner !== state.loadedUid && !p.archive && state.sharedProjectIds.includes(p.id)
      );
      
      return {
        projects,
        activeProjects,
        ownedProjects,
        sharedProjects
      };
    });
  },

  // Update a collection in projects (for when collection is renamed, modified, etc.)
  updateCollectionInProjects: (collection: CollectionInterface) => {
    set((state) => {
      if (!collection.associatedProjectId) return state;
      
      // Update the collection in the specified project
      const projects = state.projects.map(project => {
        if (project.id === collection.associatedProjectId) {
          const collections = project.collections || [];
          const updatedCollections = collections.map(c => 
            c.id === collection.id ? collection : c
          );
          
          // If collection doesn't exist in the project, add it
          if (!collections.some(c => c.id === collection.id)) {
            updatedCollections.push(collection);
          }
          
          syncToProjectDetails(project.id, project.boards || [], updatedCollections);
          return { ...project, collections: updatedCollections };
        }
        return project;
      });
      
      const activeProjects = projects.filter(p => !p.archive);
      const ownedProjects = projects.filter(p => p.owner === state.loadedUid && !p.archive);
      const sharedProjects = projects.filter(p => 
        p.owner !== state.loadedUid && !p.archive && state.sharedProjectIds.includes(p.id)
      );
      
      return {
        projects,
        activeProjects,
        ownedProjects,
        sharedProjects
      };
    });
  },

  // Remove a collection from all projects (for when collection is deleted)
  removeCollectionFromProjects: (collectionId: string) => {
    set((state) => {
      // Remove the collection from all projects that contain it
      const projects = state.projects.map(project => {
        if (project.collections && project.collections.some(c => c.id === collectionId)) {
          const updatedCollections = project.collections.filter(c => c.id !== collectionId);
          syncToProjectDetails(project.id, project.boards || [], updatedCollections);
          
          return {
            ...project,
            collections: updatedCollections
          };
        }
        return project;
      });
      
      const activeProjects = projects.filter(p => !p.archive);
      const ownedProjects = projects.filter(p => p.owner === state.loadedUid && !p.archive);
      const sharedProjects = projects.filter(p => 
        p.owner !== state.loadedUid && !p.archive && state.sharedProjectIds.includes(p.id)
      );
      
      return {
        projects,
        activeProjects,
        ownedProjects,
        sharedProjects
      };
    });
  }


}));

export const useProjectSchemaDetails = create<ProjectSchemaDetailsState>((set, get) => ({
  projectsData: {},
  loadedUids: {},
  userProjects: [],
  ownedProjects: [],
  sharedProjects: [],

  setProject: (projectId: string, project: ProjectSchemeInterface) => set((state) => ({
    projectsData: {
      ...state.projectsData,
      [projectId]: {
        ...(state.projectsData[projectId] || { boards: [], collections: [], isLoading: false }),
        project
      }
    }
  })),

  setBoards: async (projectId: string, newBoards: BoardSchemeInterface[], uid: string, permissions: Permissions | null) => {
    // If no uid provided or projectId is missing, return
    if (!projectId || !uid) {
      console.error('Missing required parameters for setBoards: projectId or uid');
      return;
    }
    
    const state = get();
    
    // If this project doesn't exist in the state yet, return
    if (!state.projectsData[projectId]) {
      console.warn(`Project with ID ${projectId} not found in state.`);
      return;
    }
    
    try {
      // Since boards are now associated via associatedProjectId field,
      // we don't need to update the project document anymore
      // Just update local state
      set((state) => ({
        projectsData: {
          ...state.projectsData,
          [projectId]: {
            ...state.projectsData[projectId],
            // Only keep non-archived boards in the state
            boards: newBoards.filter(board => board.archive !== true)
          }
        }
      }));
      
      // Also update the main project store
      const projectStore = useProjectSchemas.getState();
      const projects = projectStore.projects.map(p => 
        p.id === projectId 
          ? { ...p, boards: newBoards.filter(board => board.archive !== true) }
          : p
      );
      
      const activeProjects = projects.filter(p => !p.archive);
      const ownedProjects = projects.filter(p => p.owner === projectStore.loadedUid && !p.archive);
      const sharedProjects = projects.filter(p => 
        p.owner !== projectStore.loadedUid && !p.archive && projectStore.sharedProjectIds.includes(p.id)
      );
      
      useProjectSchemas.setState({ 
        projects,
        activeProjects,
        ownedProjects,
        sharedProjects
      });
      
    } catch (error) {
      console.error('Error updating boards for project:', error);
    }
  },

  setCollections: async (projectId: string, newCollections: CollectionInterface[], uid: string, permissions: Permissions | null) => {
    // If no uid provided or projectId is missing, return
    if (!projectId || !uid) {
      console.error('Missing required parameters for setCollections: projectId or uid');
      return;
    }
    
    const state = get();
    
    // If this project doesn't exist in the state yet, return
    if (!state.projectsData[projectId]) {
      console.warn(`Project with ID ${projectId} not found in state.`);
      return;
    }

    try {
      // Update local state
      set((state) => ({
        projectsData: {
          ...state.projectsData,
          [projectId]: {
            ...state.projectsData[projectId],
            collections: newCollections
          }
        }
      }));

      // Also update the main project store
      const projectStore = useProjectSchemas.getState();
      const projects = projectStore.projects.map(p => 
        p.id === projectId 
          ? { ...p, collections: newCollections }
          : p
      );
      
      const activeProjects = projects.filter(p => !p.archive);
      const ownedProjects = projects.filter(p => p.owner === projectStore.loadedUid && !p.archive);
      const sharedProjects = projects.filter(p => 
        p.owner !== projectStore.loadedUid && !p.archive && projectStore.sharedProjectIds.includes(p.id)
      );
      
      useProjectSchemas.setState({ 
        projects,
        activeProjects,
        ownedProjects,
        sharedProjects
      });

    } catch (error) {
      console.error('Error updating collections for project:', error);
    }
  },
  
  getProjectDetails: async (project: ProjectSchemeInterface, uid: string, permissions: Permissions | null) => {
    const { projectsData, loadedUids } = get();
    const projectId = project.id;
    
    if (!projectId || !uid) return;
    
    // Check if we already loaded this project with this uid
    if (loadedUids[projectId] === uid && projectsData[projectId]) {
      // Data already loaded for this project and uid
      return;
    }
    
    // If we're already loading this project, don't start another load
    if (projectsData[projectId]?.isLoading) return;
    
    try {
      // Set loading state for this specific project
      set((state) => ({
        projectsData: {
          ...state.projectsData,
          [projectId]: {
            ...(state.projectsData[projectId] || { 
              project: {} as ProjectSchemeInterface,
              boards: [],
              collections: []
            }),
            isLoading: true
          }
        }
      }));
      
      // Fetch boards using the new API that queries by associatedProjectId
      let boards: BoardSchemeInterface[] = [];
      boards = await getBoardsForProject(project.id, uid, permissions);
      boards = boards.filter((board) => !board.archive);

      let collections: CollectionInterface[] = [];
      collections = await getCollectionsForProject(project.id, uid, permissions);

      const projectSchemasState = useProjectSchemas.getState();
      let userProjects: ProjectSchemeInterface[] = [];
      let ownedProjects: ProjectSchemeInterface[] = [];
      let sharedProjects: ProjectSchemeInterface[] = [];

      // Check if projects are already loaded in useProjectSchemas
      if (projectSchemasState.loadedUid === uid && projectSchemasState.projectsLoaded) {
        // Use cached projects from useProjectSchemas
        userProjects = projectSchemasState.activeProjects;
        ownedProjects = projectSchemasState.ownedProjects.filter(p => !p.archive);
        sharedProjects = projectSchemasState.sharedProjects.filter(p => !p.archive);
      } else {
        // If not loaded, we need to fetch them
        const sharedProjectIds = projectSchemasState.sharedProjectIds;
        const allProjects = await getProjectsScheme(uid, sharedProjectIds);
        userProjects = allProjects.filter((project) => !project.archive);
        ownedProjects = allProjects.filter(p => p.owner === uid && !p.archive);
        sharedProjects = allProjects.filter(p => p.owner !== uid && sharedProjectIds.includes(p.id) && !p.archive);
      }

      // Update state with loaded data for this specific project
      set((state) => ({
        projectsData: {
          ...state.projectsData,
          [projectId]: {
            project,
            boards,
            collections,
            isLoading: false
          }
        },
        loadedUids: {
          ...state.loadedUids,
          [projectId]: uid
        },
        userProjects,
        ownedProjects,
        sharedProjects
      }));
      
      // Also update the main project store's boards
      const projectStore = useProjectSchemas.getState();
      const projects = projectStore.projects.map(p => 
        p.id === projectId ? { ...p, boards } : p
      );
      
      const activeProjects = projects.filter(p => !p.archive);
      const ownedProjectsList = projects.filter(p => p.owner === projectStore.loadedUid && !p.archive);
      const sharedProjectsList = projects.filter(p => 
        p.owner !== projectStore.loadedUid && !p.archive && projectStore.sharedProjectIds.includes(p.id)
      );
      
      useProjectSchemas.setState({ 
        projects,
        activeProjects,
        ownedProjects: ownedProjectsList,
        sharedProjects: sharedProjectsList
      });
      
    } catch (error) {
      console.error('Error fetching project details:', error);
      
      // Update error state for this specific project
      set((state) => ({
        projectsData: {
          ...state.projectsData,
          [projectId]: {
            ...(state.projectsData[projectId] || { 
              project: {} as ProjectSchemeInterface,
              boards: [],
              collections: []
            }),
            isLoading: false
          }
        }
      }));
    }
  },
  
  getProjectById: (projectId: string) => {
    const { projectsData } = get();
    return projectsData[projectId] || null;
  },

  // New method to sync user projects from useProjectSchemas
  syncUserProjects: () => {
    const projectSchemasState = useProjectSchemas.getState();
    if (projectSchemasState.projectsLoaded) {
      const activeProjects = projectSchemasState.activeProjects;
      const ownedProjects = projectSchemasState.ownedProjects.filter(p => !p.archive);
      const sharedProjects = projectSchemasState.sharedProjects.filter(p => !p.archive);
      set({
        userProjects: activeProjects,
        ownedProjects,
        sharedProjects
      });
    }
  }
}));

export const useProjectState = create<ProjectManagementState>((set) => ({
  selectedProject: null,
  designFirms: [],
  teamMembers: [],
  associatedProjectsList: [],
  error: null,

  setProjectData: (data) => set(data),
  
  resetProjectData: () =>
    set({
      selectedProject: null,
      designFirms: [],
      teamMembers: [],
      associatedProjectsList: [],
      error: null,
    }),
  
  fetchProjectDataById: async (id: string, uid: string, permissions: Permissions | null) => {
    try {
      // First, fetch the project to check ownership
      const selectedProject = await getProjectSchemeById(id);
      
      if (!selectedProject) {
        throw new Error("Project not found");
      }

      // Now check if user has access (either owner or has permissions)
      const hasAccess = hasProjectAccess(id, uid, permissions, selectedProject);

      if (!hasAccess) {
        throw new Error("You do not have permission to access this project");
      }

      const [designFirms, associatedProjectsList] = await Promise.all([
        getDesignFirmsOfUser(uid, firebaseHelper.db, true),
        getAssociatedProjects(id),
      ]);

      let teamMembers: any[] = [];
      if(designFirms?.length > 0) {
        teamMembers = await getDesignersWithAccess(designFirms[0], firebaseHelper.db, true);
      }

      set({
        selectedProject,
        designFirms,
        teamMembers,
        associatedProjectsList,
      });
    } catch (error) {
      console.error("Error fetching project state:", error);
      set({ 
        error: error instanceof Error ? error.message : "Error fetching project data, please try again or contact studio@mattoboard.com" 
      });    
    }
  },  
}));